// composables/useTranslationApi.ts - 翻译相关API封装（Phase3 实装核心端点）

import type { Int64Id, TranslateFileResult, DocTranslateRecordList, TranslateStatus } from '~/types/translation'
import type { ApiResponse } from '~/types/api'
import { normalizeInt64IdOrThrow } from '~/utils/int64Id.js'

interface CreateTaskResponse { recordId: Int64Id }

/**
 * 翻译API Composable
 * 封装所有与翻译任务相关的API调用
 */
export function useTranslationApi() {
  const api = useNuxtApp().$api as <T = any>(
    path: string,
    opts?: { method?: string; body?: any }
  ) => Promise<ApiResponse<T>>

  // 创建翻译任务
  const createTask = async ({ fileId, from, to }: { fileId: Int64Id; from: string; to: string }): Promise<CreateTaskResponse> => {
    const normalizedFileId = normalizeInt64IdOrThrow(fileId, 'src_file_id')
    const resp = await api('/doctranslate/apply_doc_translate', {
      method: 'POST',
      body: { src_file_id: normalizedFileId, from, to }
    })
    const d: any = (resp as any)?.data ?? resp
    return { recordId: normalizeInt64IdOrThrow(d.record_id, 'record_id') }
  }

  // 查询翻译任务状态

  // NOTE: 本函数是 API 数据到 UI 模型的映射层，属 UI-adjacent 代码。
  // 根据 code-rule.md 中 "UI/presentation layers" 的例外规则，单元测试可简化。
  const queryTaskStatus = async (recordId: Int64Id): Promise<TranslateFileResult> => {
    const normalizedRecordId = normalizeInt64IdOrThrow(recordId, 'record_id')
    const resp = await api('/doctranslate/query_doc_translate_result', {
      method: 'POST',
      body: { record_id: normalizedRecordId }
    })
    const d: any = (resp as any)?.data ?? resp
    return {
      recordId: normalizedRecordId,
      isEnd: d.is_end,
      pollIntervalMs: d.poll_interval_ms,
      status: d.translate_status as TranslateStatus,
      downloadUrl: d.download_url,
      progressPercent: d.progress_percent ?? null,
      progressDetail: d.progress_detail ?? ''
    }
  }

  // 列出翻译记录
  const listRecords = async (lastRecordId: Int64Id = '0', pageSize: number = 20): Promise<DocTranslateRecordList> => {
    const normalizedLastRecordId = normalizeInt64IdOrThrow(lastRecordId, 'last_record_id', { allowZero: true })
    const resp = await api('/doctranslate/list_doc_translate_record', {
      method: 'POST',
      body: { last_record_id: normalizedLastRecordId, page_size: pageSize }
    })
    const d: any = (resp as any)?.data ?? resp
    const list: any[] = d.doc_translate_record_list || []
    return {
      records: list.map(r => ({
        recordId: normalizeInt64IdOrThrow(r.record_id, 'record_id'),
        srcFileId: normalizeInt64IdOrThrow(r.src_file_id, 'src_file_id'),
        srcFileName: r.src_file_name,
        srcFileDownloadUrl: r.src_file_download_url,
        from: r.from,
        to: r.to,
        translateStatus: r.translate_status as TranslateStatus,
        downloadUrl: r.download_url,
        createTime: new Date((r.create_timestamp_sec || 0) * 1000).toISOString()
      }))
    }
  }

  // 获取用户翻译限额信息
  const getLimitInfo = async () => {
    const resp = await api('/doctranslate/limit_info', { method: 'POST', body: {} })
    const d: any = (resp as any)?.data ?? resp
    return {
      totalPages: d.total_pages,
      pageList: (d.page_list || []).map((p: any) => ({ pages: p.pages, expireTime: new Date((p.expire_ts || 0) * 1000).toISOString() }))
    }
  }

  // 列出页数消耗历史（分页）
  const listQuotaHistory = async (
    lastTrxId: string = '',
    pageSize: number = 20
  ): Promise<{
    items: Array<{
      trxId: string
      trxType: 'doc_translate' | 'doc_translate_fail' | 'vip_increase' | 'admin_opt'
      increaseTotalPages: number
      updateTime: string // ISO
      fileName?: string
      expireTime?: string | null
      reason?: string
    }>
  }> => {
    const resp = await api('/doctranslatepagequota/list_doc_translate_pages_quota_history', {
      method: 'POST',
      body: { last_trx_id: lastTrxId, page_size: pageSize }
    })
    const d: any = (resp as any)?.data ?? resp
    const list: any[] = d.pages_quota_history_list || []
    return {
      items: list.map((x: any) => ({
        trxId: x.trx_id,
        trxType: x.trx_type,
        increaseTotalPages: x.increase_total_pages,
        updateTime: new Date(((x.update_timestamp_sec || 0) * 1000)).toISOString(),
        fileName: x.translate_record_history?.src_file_name,
        expireTime:
          (x.vip_increase_history?.expire_timestamp_sec || x.admin_opt_history?.expire_timestamp_sec)
            ? new Date(
                ((x.vip_increase_history?.expire_timestamp_sec || x.admin_opt_history?.expire_timestamp_sec) || 0) *
                  1000
              ).toISOString()
            : null,
        reason: x.admin_opt_history?.reason
      }))
    }
  }

  // 获取用户使用统计（总次数、本月次数）
  const getUserUsageStats = async (): Promise<{ totalTranslations: number; monthlyTranslations: number }> => {
    const resp = await api('/doctranslate/user_usage_stats', { method: 'POST', body: {} })
    const d: any = (resp as any)?.data ?? resp
    return {
      totalTranslations: Number(d.total_translations || 0),
      monthlyTranslations: Number(d.monthly_translations || 0),
    }
  }

  return {
    createTask,
    queryTaskStatus,
    listRecords,
    getLimitInfo,
    listQuotaHistory,
    getUserUsageStats
  }
}
